'use client';

import { memo } from 'react';
import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  Position,
  useReactFlow,
  type EdgeProps,
  type Node,
} from '@xyflow/react';
import { Box, token } from '@terra/ui-styled-system';
import { MetaDefault } from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import type {
  FindingAttackPathFlowEdgeData,
  FindingAttackPathFlowNodeData,
} from './build-finding-attack-path-flow';
import { findingAttackPathEntityColor } from './finding-attack-path-entity-color';

const ENERGY_STROKE = token('colors.fg.default');

function energyDashForLength(edgeLength: number): string {
  if (edgeLength < 140) {
    return '0.28 0.72';
  }

  if (edgeLength < 220) {
    return '0.18 0.82';
  }

  return '0.12 0.88';
}

function FindingAttackPathEdgeComponent({
  id,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
}: EdgeProps) {
  const t = useTranslations('findingAttackPath.relations');
  const { getNode } = useReactFlow<Node<FindingAttackPathFlowNodeData>>();
  const edgeData = (data ?? {}) as FindingAttackPathFlowEdgeData;
  const targetKind = getNode(target)?.data.entityKind ?? '';
  const stroke = findingAttackPathEntityColor(targetKind);
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition: sourcePosition ?? Position.Right,
    targetX,
    targetY,
    targetPosition: targetPosition ?? Position.Left,
    curvature: 0.35,
  });
  const inferred = edgeData.variant === 'inferred';
  const edgeLength = Math.hypot(targetX - sourceX, targetY - sourceY);

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        style={{
          stroke,
          strokeWidth: 2,
          strokeDasharray: inferred ? '6 10' : undefined,
          ...(inferred
            ? {
                animation:
                  'finding-attack-path-edge-dash 1.25s linear infinite',
              }
            : {}),
        }}
        interactionWidth={18}
      />
      {inferred ? null : (
        <path
          d={edgePath}
          fill="none"
          pathLength={1}
          stroke={ENERGY_STROKE}
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeDasharray={energyDashForLength(edgeLength)}
          opacity={0.55}
          style={{
            pointerEvents: 'none',
            animation: 'finding-attack-path-edge-energy 1.4s linear infinite',
          }}
        />
      )}
      {edgeData.relation ? (
        <EdgeLabelRenderer>
          <Box
            position="absolute"
            pointerEvents="none"
            style={{
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            }}
          >
            <Box
              px="2"
              py="0.5"
              borderRadius="sm"
              bg="bg.default"
              borderWidth="1px"
              borderColor="border.subtle"
            >
              <MetaDefault color="fg.muted">{t(edgeData.relation)}</MetaDefault>
            </Box>
          </Box>
        </EdgeLabelRenderer>
      ) : null}
    </>
  );
}

export const FindingAttackPathEdge = memo(FindingAttackPathEdgeComponent);
